import debounce from 'debounce-promise';

import Common from './Common';
import Utils from '../utils/utils';
import Storage from '../utils/Storage';

import { handleUnauthorized } from '../actions/auth';
import { heFetch } from './HeFetch';
import getTrackingPageName from './tracking-page-name.config';
import i18n from '../i18n';

export default class Search {
    static requestOnce = debounce(Search.sendRequest, 200);
    static additionalRequestOnce = debounce(Search.sendRequest, 200);

    static applyPetSittingFilter(filters) {
        if (!filters?.home || !('pets_to_care_for' in filters.home)) {
            return filters;
        }

        const allAnimals = ['cat', 'dog', 'other'];
        const raw = filters.home.pets_to_care_for;
        const source = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : [];
        const sitting = source.filter((animal) => allAnimals.includes(animal));

        if (sitting.length === 0) {
            const home = { ...filters.home };
            delete home.pets_to_care_for;
            return { ...filters, home };
        }

        const excluded = allAnimals.filter((animal) => !sitting.includes(animal));
        const currentExcluded = (filters.home.exclude_animals || []).filter((animal) =>
            allAnimals.includes(animal)
        );

        return {
            ...filters,
            home: {
                ...filters.home,
                exclude_animals: _.union(currentExcluded, excluded),
                pets_to_care_for: true
            }
        };
    }

    /*
     * Execute search query and abort a previous query if exists
     */
    static findHomes(
        filters,
        offset,
        limit,
        place,
        placeId,
        lastSearchId,
        isAdditional = false,
        accessToken,
        dispatch
    ) {
        const request = isAdditional ? Search.additionalRequestOnce : Search.requestOnce;
        return request(filters, offset, limit, place, placeId, lastSearchId, accessToken, dispatch, false);
    }

    static sendRequest(
        filters,
        offset,
        limit,
        place,
        placeId,
        lastSearchId,
        accessToken,
        dispatch,
        versionLegacy = true
    ) {
        filters = filters || {};
        delete filters.placeId;
        filters = Utils.filterObjectRecursive(filters);
        filters = Search.applyPetSittingFilter(filters);

        let url = `/search/homes`;
        const params = $.param(
            Utils.filterObjectRecursive({
                offset,
                limit
            })
        );

        if (!_.isEmpty(params)) {
            url = `${url}?${params}`;
        }

        const lastSearchModel = { place };
        if (lastSearchId) {
            lastSearchModel.id = lastSearchId;
        }
        if (placeId) {
            lastSearchModel.place_id = placeId;
        }
        // eslint-disable-next-line camelcase
        const last_search = place ? lastSearchModel : null;
        // eslint-disable-next-line camelcase
        const data = last_search ? { last_search, search_query: filters } : { search_query: filters };

        const lastSearch = {
            id: lastSearchId,
            place
        };

        if (typeof placeId === 'string' && placeId !== 'false') {
            lastSearch.place_id = placeId;
        }

        const baseHeaders = {
            'X-SEARCH-API-VERSION': 'v2',
            'X-LEGACY-RESPONSE': versionLegacy,
            'Accept-Language': i18n.language
        };

        if (Search.hasFunctionalConsent()) {
            baseHeaders['x-functional-consent-cookie'] = '1';
        }

        if (accessToken) {
            return handleUnauthorized(dispatch, async () => {
                return heFetch.post(url, {
                    data: JSON.stringify(data),
                    headers: {
                        ...baseHeaders,
                        'X-HE-PAGE-NAME': getTrackingPageName(
                            window?.location?.pathname,
                            Search.isRecommendationSearch() ? 'RECOMMENDATION' : ''
                        )
                    }
                });
            });
        } else {
            return heFetch.post(url, {
                data: JSON.stringify(data),
                headers: {
                    ...baseHeaders,
                    'X-HE-PAGE-NAME': getTrackingPageName(window?.location?.pathname)
                }
            });
        }
    }

    static getRecommendations(filters, place, placeId, lastSearchId, accessToken, dispatch) {
        filters = filters || {};
        delete filters.placeId;
        filters = Utils.filterObjectRecursive(filters);
        filters = Search.applyPetSittingFilter(filters);

        let url = `/search/recommendation`;
        const params = $.param(
            Utils.filterObjectRecursive({
                limit: 8
            })
        );

        if (!_.isEmpty(params)) {
            url = `${url}?${params}`;
        }

        const lastSearchModel = { place };
        if (lastSearchId) {
            lastSearchModel.id = lastSearchId;
        }
        if (placeId) {
            lastSearchModel.place_id = placeId;
        }
        // eslint-disable-next-line camelcase
        const last_search = place ? lastSearchModel : null;
        // eslint-disable-next-line camelcase
        const data = last_search ? { last_search, search_query: filters } : { search_query: filters };

        const lastSearch = {
            id: lastSearchId,
            place
        };

        if (typeof placeId === 'string' && placeId !== 'false') {
            lastSearch.place_id = placeId;
        }

        if (accessToken) {
            return handleUnauthorized(dispatch, async () => {
                return heFetch
                    .post(url, {
                        data: JSON.stringify(data),
                        headers: {
                            'X-SEARCH-API-VERSION': 'v2',
                            'X-HE-PAGE-NAME': getTrackingPageName(window?.location?.pathname)
                        }
                    })
                    .then((results) => {
                        return results;
                    });
            });
        }
        return Promise.resolve({ homes: [], total: 0, see_more: { queryParams: '' } });
    }

    static getLastResearches(nbResearches = 1) {
        return Common.request(
            {
                url: `/v1/search/last?version=2`
            },
            null,
            null,
            nbResearches
        );
    }

    static getLastSearch() {
        const params = Common.params(
            {
                url: '/v1/search/last',
                method: 'get'
            },
            null,
            null,
            1
        );
        return Common.request(params).then((results) => {
            if (Array.isArray(results) && results.length > 0) {
                return results[0];
            }
            return null;
        });
    }

    static setLastSearch(userId, data) {
        data.query = data.query ? data.query : 'everywhere';
        Storage.set(`${userId}_search`, data);
    }

    static getLocalLastSearch(userId) {
        const deferred = $.Deferred();
        deferred.resolve(Storage.get(`${userId}_search`));
        return deferred.promise();
    }

    /*
     * Whether the visitor granted OneTrust functional consent (category C0003).
     * Primary source: window.OnetrustActiveGroups, fallback: the OptanonConsent cookie
     * Any missing/unreadable signal counts as a refusal.
     */
    static hasFunctionalConsent() {
        const FUNCTIONAL = 'C0003';

        const activeGroups = typeof window !== 'undefined' ? window.OnetrustActiveGroups : undefined;
        if (typeof activeGroups === 'string') {
            return activeGroups.split(',').includes(FUNCTIONAL);
        }

        const optanonConsent = Utils.readCookie('OptanonConsent');
        if (optanonConsent) {
            const groups = new URLSearchParams(optanonConsent).get('groups');
            if (groups) {
                return groups.split(',').some((entry) => {
                    const [code, state] = entry.split(':');
                    return code === FUNCTIONAL && state === '1';
                });
            }
        }

        return false;
    }

    static isRecommendationSearch() {
        const params = new URLSearchParams(window?.location?.search);
        return params.get('search_type') === 'recommendation';
    }
}
